import { Box, Stack, Typography } from '@mui/material';
import {
  getMediaBasePath,
  ImageWrapper,
  TypographyHtml,
} from '@geneo2-web/shared-ui';
import { QAttemptResultEnum } from '@protos/learning_management/lms.db_pb';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import React, { useMemo } from 'react';
import { IQuestionMetadataSolutionStep } from '../../api/learners';

export interface DisplayAnswer {
  text?: { optionText?: string };
  image?: { image?: { imageUrl?: string; imageName?: string } };
  isCorrect?: QAttemptResultEnum;
}

export const AnswerDisplayRow: React.FC<{
  answer: DisplayAnswer;
  variant: 'submitted' | 'correct';
  showMissedBadge?: boolean;
}> = ({ answer, variant, showMissedBadge }) => {
  const isCorrect =
    variant === 'correct' ||
    answer.isCorrect === QAttemptResultEnum.RESPONSE_CORRECT;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        bgcolor: isCorrect ? '#dcfce7' : '#fef2f2',
        borderRadius: '8px',
        px: 2,
        py: 1.25,
        border: `1px solid ${isCorrect ? '#22c55e' : '#ef4444'}`,
      }}
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: 1,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: isCorrect ? '#16a34a' : '#ef4444',
        }}
      >
        {isCorrect ? (
          <CheckIcon sx={{ fontSize: 16, color: 'white' }} />
        ) : (
          <CloseIcon sx={{ fontSize: 16, color: 'white' }} />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {!!answer.image && (
          <ImageWrapper
            path={getMediaBasePath(
              answer.image?.image?.imageUrl,
              'processedMediaBucket'
            )}
            name={answer.image?.image?.imageName || 'image'}
            styles={{ width: '80%' }}
          />
        )}
        {!!answer.text && (
          <TypographyHtml
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: '#1e293b',
              lineHeight: 1.4,
            }}
          >
            {answer.text?.optionText || ''}
          </TypographyHtml>
        )}
      </Box>
      {showMissedBadge && (
        <Typography
          sx={{
            fontSize: 9,
            fontWeight: 700,
            color: '#dc2626',
            bgcolor: '#fee2e2',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            flexShrink: 0,
            lineHeight: 1.6,
          }}
        >
          MISSED
        </Typography>
      )}
    </Box>
  );
};

const sectionLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  color: '#64748b',
  letterSpacing: 1,
  textTransform: 'uppercase' as const,
};

export const UserAnswerSection: React.FC<{
  answers: DisplayAnswer[];
}> = ({ answers }) => {
  if (answers.length === 0) return null;
  return (
    <Box sx={{ py: 3, borderBottom: '1px solid #ddd' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box component="span" sx={{ fontSize: 15, lineHeight: 1 }}>
          🧑
        </Box>
        <Typography sx={sectionLabelSx}>Your Answer</Typography>
      </Box>
      <Stack spacing={1}>
        {answers.map((val, index) => (
          <AnswerDisplayRow
            key={'submitted_' + index}
            answer={val}
            variant="submitted"
          />
        ))}
      </Stack>
    </Box>
  );
};

export const CorrectAnswerSection: React.FC<{
  correctAnswers: DisplayAnswer[][];
  userSubmittedAnswers?: DisplayAnswer[];
}> = ({ correctAnswers, userSubmittedAnswers }) => {
  if (correctAnswers.length === 0) return null;

  const submittedTextsSet = useMemo(
    () =>
      new Set(
        (userSubmittedAnswers ?? [])
          .map((a) => a.text?.optionText?.trim().toLowerCase())
          .filter(Boolean)
      ),
    [userSubmittedAnswers]
  );

  return (
    <Box sx={{ py: 3, borderBottom: '1px solid #ddd' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <CheckIcon sx={{ fontSize: 16, color: '#16a34a' }} />
        <Typography sx={sectionLabelSx}>Correct Answer</Typography>
      </Box>
      <Stack spacing={1}>
        {correctAnswers.map((group, index) => (
          <React.Fragment key={'correct_group_' + index}>
            <Box>
              {group.map((val, idx) => {
                const optText =
                  val.text?.optionText?.trim().toLowerCase() || '';
                const wasMissed =
                  optText !== '' && !submittedTextsSet.has(optText);
                return (
                  <Box
                    key={'corr_' + index + '_' + idx}
                    sx={{ mb: idx !== group.length - 1 ? 0.75 : 0 }}
                  >
                    <AnswerDisplayRow
                      answer={val}
                      variant="correct"
                      showMissedBadge={wasMissed}
                    />
                  </Box>
                );
              })}
            </Box>
            {index !== correctAnswers.length - 1 && (
              <Typography
                sx={{
                  textAlign: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#94a3b8',
                  my: 0.75,
                }}
              >
                OR
              </Typography>
            )}
          </React.Fragment>
        ))}
      </Stack>
    </Box>
  );
};

export const SolutionSection: React.FC<{
  solution: IQuestionMetadataSolutionStep[];
}> = ({ solution }) => {
  if (solution.length === 0) return null;
  return (
    <Box sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Box component="span" sx={{ fontSize: 15, lineHeight: 1 }}>
          📚
        </Box>
        <Typography sx={sectionLabelSx}>Solution Explained</Typography>
      </Box>
      <Stack spacing={3}>
        {solution.map((step, index) => (
          <Box
            key={'solution_' + index}
            sx={{
              pl: 2,
              borderLeft: '3px solid #22c55e',
              bgcolor: '#f8fafc',
              borderRadius: '8px',
              py: 1,
            }}
          >
            {step.description && (
              <TypographyHtml
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#1e293b',
                  mb: 0.75,
                }}
              >
                {step.description}
              </TypographyHtml>
            )}
            {step.explanation && (
              <TypographyHtml
                sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}
              >
                {step.explanation}
              </TypographyHtml>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
